import type { Testimonial } from "@/mocks/testimonials";

export interface ReviewAggregate {
  average: number;
  count: number;
}

/**
 * Computes the aggregate rating from the reviews that are actually rendered on
 * the page. Only rated reviews count, so the markup always matches visible
 * content (a hard requirement for valid review snippets).
 */
export function computeReviewAggregate(reviews: Testimonial[]): ReviewAggregate | null {
  const rated = reviews.filter((review) => Number.isFinite(review.rating) && review.rating > 0);
  if (rated.length === 0) return null;
  const sum = rated.reduce((total, review) => total + review.rating, 0);
  const average = Math.round((sum / rated.length) * 10) / 10;
  return { average, count: rated.length };
}

export interface ReviewSchemaOptions {
  name: string;
  url: string;
  image?: string;
}

/**
 * Builds a schema.org LocalBusiness node carrying the aggregate rating and the
 * individual reviews shown on the page. This is the markup that lets search
 * engines surface star ratings and review snippets — FAQPage cannot do that.
 * Returns null when there are no rated reviews so we never emit empty markup.
 */
export function buildReviewSchema(
  reviews: Testimonial[],
  options: ReviewSchemaOptions,
): Record<string, unknown> | null {
  const aggregate = computeReviewAggregate(reviews);
  if (!aggregate) return null;

  const rated = reviews.filter((review) => Number.isFinite(review.rating) && review.rating > 0);

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: options.name,
    url: options.url,
    ...(options.image ? { image: options.image } : {}),
    address: {
      "@type": "PostalAddress",
      addressLocality: "Konaseema",
      addressRegion: "Andhra Pradesh",
      addressCountry: "IN",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: aggregate.average,
      reviewCount: aggregate.count,
      bestRating: 5,
      worstRating: 1,
    },
    review: rated.map((review) => ({
      "@type": "Review",
      author: { "@type": "Person", name: review.name },
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
      ...(review.experience
        ? { itemReviewed: { "@type": "Thing", name: review.experience } }
        : {}),
      reviewBody: review.quote,
    })),
  };
}